class CartRemoveButton extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', (event) => this.handleRemove(event));
  }

  async handleRemove(event) {
    event.preventDefault();
    
    const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
    
    // First update the cart UI
    await cartItems.updateQuantity(this.dataset.index, 0);
    
    // Then immediately fetch latest cart data and update counter
    const response = await fetch('/cart.js');
    const cart = await response.json();
    
    const homeTryOnItems = cart.items.filter(item => item.properties?.home_try_on);
    const homeTryOnSection = document.querySelector('.cart-home-try-on-section');
    
    if (homeTryOnSection) {
      const itemCount = homeTryOnItems.length;
      let counterDiv = homeTryOnSection.querySelector('.home-try-on-counter');
      let warningMessage = '';
      
      if (itemCount === 0) {
        homeTryOnSection.style.display = 'none';
      } else if (itemCount < 4) {
        warningMessage = `Please add at least 4 items for Home Try On. You currently have ${itemCount} ${itemCount === 1 ? 'item' : 'items'}.`;
      } else if (itemCount > 6) {
        warningMessage = 'Please remove some items. Maximum limit is 6 items for Home Try On.';
      }

      if (counterDiv) {
        counterDiv.innerHTML = `
          <p>Home Try On Items: ${itemCount}/6</p>
          ${warningMessage ? `<p class="warning">${warningMessage}</p>` : ''}
        `;
      }
    }

    // Finally trigger the main cart update
    window.cart?.updateCart();
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement = document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');
    
    if (document.querySelector('.cart-shipping')) {
      this.minSpend = document.querySelector('.cart-shipping').dataset.minSpend;
      this.minTotal = Math.round(this.minSpend * (Shopify.currency.rate || 1));
      this.cartShipping();
    }

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  cartShipping() {
    let progressPrev = getComputedStyle(document.querySelector('.cart-shipping__progress-current')).getPropertyValue('width');    
    document.documentElement.style.setProperty('--progress-prev', progressPrev);

    this.total = document.querySelector('.cart-shipping').dataset.total;
    this.progress = this.total / this.minTotal * 100;
    if (this.progress > 100)
      this.progress = 100;

    if (this.minTotal > this.total) {
      let amount = this.minTotal - this.total;
      let message = document.querySelector('.cart-shipping').dataset.message.replace('||amount||', formatMoney(amount));
      document.querySelector('.cart-shipping__message_default').innerText = message;
      document.querySelector('.cart-shipping__message_success').classList.remove('active');
      document.querySelector('.cart-shipping__message_default').classList.add('active');
    }
    else {
      document.querySelector('.cart-shipping__message_default').classList.remove('active');
      document.querySelector('.cart-shipping__message_success').classList.add('active');
    }
    document.querySelector('.cart-shipping__progress-current').style.width = this.progress+'%';
  }

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  onChange(event) {
    this.updateQuantity(event.target.dataset.index, event.target.value, document.activeElement.getAttribute('name'));
  }

  onCartUpdate() {
    fetch(`${routes.cart_url}?section_id=main-cart-items`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const sourceQty = html.querySelector('cart-items');
        this.innerHTML = sourceQty.innerHTML;
      })
      .catch(e => {
        console.error(e);
      });
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section'
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents-totals'
      },
      {
        id: 'main-cart-shipping',
        section: document.getElementById('main-cart-shipping').dataset.id || null,
        selector: '.js-contents-shipping'
      }
    ];
  }

  updateQuantity(line, quantity, name) {
    this.enableLoading(line);
    this.querySelectorAll('.quantity__button').forEach((button) => button.classList.add('disabled'));

    if (document.querySelectorAll('.card--product card__add-to-cart button[name="add"]')) {
      document.querySelectorAll('.card--product .card__add-to-cart button[name="add"]').forEach(button => {
        button.setAttribute('aria-disabled', false);
        if (button.querySelector('span')) {
          button.querySelector('span').classList.remove('hidden');
          button.querySelector('.sold-out-message').classList.add('hidden');
        }
      });
    }

    if (document.querySelector('.cart-shipping')) {
      let progressPrev = getComputedStyle(document.querySelector('.cart-shipping__progress-current')).getPropertyValue('width');
      document.documentElement.style.setProperty('--progress-prev', progressPrev);
    }

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        const quantityElement = document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll('.cart-item');
        if (parsedState.errors) {
          quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

        this.getSectionsToRender().forEach((section => {
          const elementToReplace =
            document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
          elementToReplace.innerHTML =
            this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
        }));
        const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
        let message = '';
        if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
          if (typeof updatedValue === 'undefined') {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
          }
        }
        this.updateLiveRegions(line, message);

        const lineItem = document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`)) : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'))
        } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'))
        }

        // Refresh the cart drawer to show upsell products
        if (typeof updateCartDrawer === 'function') {
          updateCartDrawer();
        }

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items' });
      }).catch(() => {
        this.querySelectorAll('.loading-overlay').forEach((overlay) => overlay.classList.add('hidden'));
        this.querySelectorAll('.quantity__button').forEach((button) => button.classList.remove('disabled'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.querySelectorAll('.quantity__button').forEach((button) => button.classList.remove('disabled'));
        if (document.querySelector('.cart-shipping')) {
          this.cartShipping();
        }
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError = document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').innerHTML = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus = document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading-overlay`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading-overlay`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading-overlay`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading-overlay`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define('cart-note', class CartNote extends HTMLElement {
    constructor() {
      super();

      this.addEventListener('change', debounce((event) => {
        const body = JSON.stringify({ note: event.target.value });
        fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
      }, ON_CHANGE_DEBOUNCE_TIMER))
    }
  });
}

class Cart {
  constructor() {
    this.setupEventListeners();
    this.updateCart();
  }

  setupEventListeners() {
    // Listen for cart updates through the Shopify events
    document.addEventListener('cart:updated', () => {
      this.updateCart();
    });

    // Listen for quantity changes
    document.addEventListener('change', (event) => {
      if (event.target.matches('.quantity__input')) {
        // Wait for the quantity update to complete
        setTimeout(() => this.updateCart(), 1000);
      }
    });

    // Listen for cart drawer updates
    document.addEventListener('DOMContentLoaded', () => {
      if (typeof window.PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
          this.updateCart();
        });
      }
    });
  }

  async updateCart() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      if (!cart) return;

      const homeTryOnItems = cart.items.filter(item => item.properties?.home_try_on);
      const homeTryOnSection = document.querySelector('.cart-home-try-on-section');
      const checkoutButton = document.querySelector('.cart__checkout-button');
      
      if (homeTryOnSection) {
        homeTryOnSection.style.display = homeTryOnItems.length > 0 ? 'block' : 'none';

        // Add or update the counter display
        let counterDiv = homeTryOnSection.querySelector('.home-try-on-counter');
        if (!counterDiv) {
          counterDiv = document.createElement('div');
          counterDiv.className = 'home-try-on-counter';
          homeTryOnSection.insertBefore(counterDiv, homeTryOnSection.firstChild);
        }
        
        const itemCount = homeTryOnItems.length;
        let warningMessage = '';
        let disableCheckout = false;

        // Determine warning message and checkout state
        if (itemCount === 0) {
          homeTryOnSection.style.display = 'none';
        } else if (itemCount < 4) {
          warningMessage = `Please add at least 4 items for Home Try On. You currently have ${itemCount} ${itemCount === 1 ? 'item' : 'items'}.`;
          disableCheckout = true;
        } else if (itemCount > 6) {
          warningMessage = 'Please remove some items. Maximum limit is 6 items for Home Try On.';
          disableCheckout = true;
        }

        // Update counter display with new count and warning
        counterDiv.innerHTML = `
          <p>Home Try On Items: ${itemCount}/6</p>
          ${warningMessage ? `<p class="warning">${warningMessage}</p>` : ''}
        `;

        // Update checkout button state
        if (checkoutButton) {
          if (disableCheckout) {
            checkoutButton.setAttribute('disabled', 'disabled');
            checkoutButton.style.opacity = '0.5';
            checkoutButton.style.cursor = 'not-allowed';
            checkoutButton.textContent = 'Home Try On Requirements Not Met';
          } else if (itemCount >= 4 && itemCount <= 6) {
            checkoutButton.removeAttribute('disabled');
            checkoutButton.style.opacity = '1';
            checkoutButton.style.cursor = 'pointer';
            checkoutButton.textContent = 'Proceed to Checkout';
          }
        }
      }

    } catch (error) {
      console.error('Error updating cart:', error);
    }
  }

  async handleHomeTryOnClick(event) {
    event.preventDefault();
    
    let selectedVariantId = null;
    
    const form = event.target.closest('form');
    if (form) {
      const variantInput = form.querySelector('input[name="id"][type="radio"]:checked, select[name="id"], input[name="id"][type="hidden"]');
      if (variantInput) {
        selectedVariantId = variantInput.value;
      }
    }
    
    if (!selectedVariantId) {
      selectedVariantId = event.target.dataset.variantId;
    }

    if (!selectedVariantId) {
      console.error('No variant selected');
      return;
    }

    try {
      // First, get current cart contents
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      
      // Check if this variant is already in the cart as a home try on item
      const isVariantInCart = cart.items.some(item => 
        item.variant_id === parseInt(selectedVariantId) && 
        item.properties && 
        item.properties.home_try_on === true
      );

      if (isVariantInCart) {
        alert('This size is already in your Home Try On cart.');
        return;
      }

      // Count total home try on items
      const homeTryOnItemsCount = cart.items.filter(item => 
        item.properties && 
        item.properties.home_try_on === true
      ).length;

      // Check minimum and maximum limits
      if (homeTryOnItemsCount >= 6) {
        alert('You can only add up to 6 items for Home Try On.');
        return;
      }

      // If checks pass, add to cart
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          items: [{
            id: parseInt(selectedVariantId),
            quantity: 1,
            properties: { 'home_try_on': true }
          }]
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();

      // Check if minimum items requirement is met
      if (homeTryOnItemsCount < 3) {
        alert(`Please add at least 4 items for Home Try On. You currently have ${homeTryOnItemsCount + 1} items.`);
      }

      // Refresh the page to show updated cart
      window.location.reload();

    } catch (error) {
      console.error('Error:', error);
      alert('Could not add item to cart. Please try again.');
    }
  }
}

// Initialize cart functionality and make it globally available
window.addEventListener('DOMContentLoaded', () => {
  window.cart = new Cart();
});